import React, { useState } from 'react';
import { useAzureFunctionService } from '../services/azureFunctionService';

// Helper functions to get default dates
const getYesterday = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

const getToday = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export const AzureFunctionCall: React.FC = () => {
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(getYesterday());
  const [endDate, setEndDate] = useState<string>(getToday());
  const [selectedPayGroup, setSelectedPayGroup] = useState<string>('');
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [stats, setStats] = useState<any | null | undefined>(undefined);
  const azureFunctionService = useAzureFunctionService();

  // Hardcoded pay groups with values and display names
  const payGroups = [
    { value: 'JCKNE', label: 'JCK' },
    { value: 'CORNE', label: 'Corporate' },
    { value: 'KBRNE', label: 'KBR' },
    { value: 'BRONE', label: 'HBros' },
    { value: 'COFNE', label: 'Coffee' },
    { value: 'SUBNE', label: 'Subs' },
    { value: 'BRZNE', label: 'Birdz' }
  ];

  const handleDownloadCsv = async () => {
    setIsCsvDownloading(true);
    setCsvError(null);

    try {
      // Validate date range
      if (new Date(startDate) > new Date(endDate)) {
        throw new Error('Start date must be before or equal to end date');
      }

      // Validate pay group selection
      if (!selectedPayGroup) {
        throw new Error('Please select a pay group');
      }

      // Call the 'harripds/export' endpoint with date range and pay group
      await azureFunctionService.downloadCsv('harripds/export', {
        startDate,
        endDate,
        payGroup: selectedPayGroup
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setCsvError(errorMessage);
    } finally {
      setIsCsvDownloading(false);
    }
  };

  const handleShowStats = async () => {
    setIsLoadingStats(true);
    setStatsError(null);
    setStats(undefined);

    try {
      // Validate date range
      if (new Date(startDate) > new Date(endDate)) {
        throw new Error('Start date must be before or equal to end date');
      }

      // Validate pay group selection
      if (!selectedPayGroup) {
        throw new Error('Please select a pay group');
      }

      // Call the 'harripds/stats' endpoint with same arguments
      const statsData = await azureFunctionService.getStats('harripds/stats', {
        startDate,
        endDate,
        payGroup: selectedPayGroup
      });
      setStats(statsData); // statsData will be null if 204 response
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setStatsError(errorMessage);
    } finally {
      setIsLoadingStats(false);
    }
  };

  return (
    <div style={{ margin: '20px 0', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}>
      {/* CSV Download Section */}
      <div style={{ 
        marginTop: '10px', 
        padding: '20px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h2>Download Harri PDS CSV</h2>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Start Date:
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '200px'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              End Date:
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '200px'
              }}
            />
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Pay Group:
            </label>
            <select
              value={selectedPayGroup}
              onChange={(e) => setSelectedPayGroup(e.target.value)}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '215px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select a pay group...</option>
              {payGroups.map((payGroup, index) => (
                <option 
                  key={index} 
                  value={payGroup.value}
                >
                  {payGroup.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button 
          onClick={handleDownloadCsv} 
          disabled={isCsvDownloading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isCsvDownloading ? 'not-allowed' : 'pointer',
            opacity: isCsvDownloading ? 0.6 : 1,
            fontSize: '14px',
            marginRight: '10px'
          }}
        >
          {isCsvDownloading ? 'Downloading CSV...' : 'Download CSV'}
        </button>

        <button 
          onClick={handleShowStats} 
          disabled={isLoadingStats}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoadingStats ? 'not-allowed' : 'pointer',
            opacity: isLoadingStats ? 0.6 : 1,
            fontSize: '14px'
          }}
        >
          {isLoadingStats ? 'Loading Stats...' : 'Show Stats'}
        </button>

        {csvError && (
          <div style={{ 
            marginTop: '10px', 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24',
            borderRadius: '4px'
          }}>
            Error: {csvError}
          </div>
        )}

        {statsError && (
          <div style={{ 
            marginTop: '10px', 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24',
            borderRadius: '4px'
          }}>
            Stats Error: {statsError}
          </div>
        )}

        {stats !== undefined && (
          <>
            {stats === null ? (
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#fff3cd', 
                color: '#856404',
                borderRadius: '4px',
                border: '1px solid #ffeaa7',
                textAlign: 'center'
              }}>
                <strong>No stats found for current selection</strong>
              </div>
            ) : (
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#e7f3ff', 
                color: '#0c5460',
                borderRadius: '4px',
                border: '1px solid #bee5eb'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#0c5460' }}>PDS Stats</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                  <div><strong>Unique Employees:</strong> {stats.UniqueEmployees?.toLocaleString() || 'N/A'}</div>
                  <div><strong>Unique Locations:</strong> {stats.UniqueLocations?.toLocaleString() || 'N/A'}</div>
                  <div><strong>Total Records:</strong> {stats.TotalRecords?.toLocaleString() || 'N/A'}</div>
                  <div><strong>Total Regular Hours:</strong> {stats.TotalRegularHours?.toLocaleString() || 'N/A'}</div>
                  <div><strong>Total Overtime Hours:</strong> {stats.TotalOvertimeHours?.toLocaleString() || 'N/A'}</div>
                  <div><strong>Total Penalties Paid:</strong> ${stats.TotalPenaltiesPaid?.toLocaleString() || 'N/A'}</div>
                </div>
                {stats.TermedEmployees && stats.TermedEmployees.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong>Termed Employees ({stats.TermedEmployees.length}):</strong>
                    <div style={{ 
                      marginTop: '5px', 
                      maxHeight: '100px', 
                      overflowY: 'auto',
                      padding: '5px',
                      backgroundColor: 'rgba(255,255,255,0.5)',
                      borderRadius: '3px'
                    }}>
                      {stats.TermedEmployees.map((employee: any, index: number) => (
                        <div key={index} style={{ fontSize: '14px', padding: '2px 0' }}>
                          {typeof employee === 'string' ? employee : employee.name || employee.id || JSON.stringify(employee)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};